import { useState } from 'react'
import BottomSheetContent from '../../../components/BottomSheetComponents/BottomSheetContent'
import { PrimaryButton } from '../../../components/execution/PrimaryButton'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { SELECT_FINANCIAL_YEAR_HEADING_ID } from './financialYearSelectionConstants'

const FINANCIAL_YEAR_RADIO_NAME = 'tax-summary-financial-year'

type FinancialYearSelectionBottomSheetProps = {
  onDismiss: () => void
  selectedFinancialYear: string
  financialYears: string[]
  onConfirm: (financialYear: string) => void
}

export default function FinancialYearSelectionBottomSheet({
  onDismiss,
  selectedFinancialYear,
  financialYears,
  onConfirm
}: FinancialYearSelectionBottomSheetProps) {
  const [selectedOption, setSelectedOption] = useState(selectedFinancialYear || (financialYears?.[0] ?? ''))

  return (
    <BottomSheetContent
      heading='Select financial year'
      headingId={SELECT_FINANCIAL_YEAR_HEADING_ID}
      onDismiss={onDismiss}
      bottomSheetBodyClassName='!px-0 !pt-2 !pb-4'
      bottomSheetHeadingClassName='!text-[22px] !font-[500] !leading-[1.2] !px-5'
      bottomSheetCtaWrapperClassName='hidden'
    >
      <div className='pt-2'>
        {/*
          Native radios inside a list (Tax summary_B14):
          - list markup supplies position ("1 of 2") without TalkBack's "Option N of M"
          - shared name groups radios without role="radiogroup" ("In group")
          - one focus per row: radio covers the row; aria-label = year (matches visible text)
          - explicit list roles: list-style:none drops list semantics in TalkBack/WebKit
        */}
        <ul role='list' className='list-none m-0 p-0' aria-label='Select financial year'>
          {financialYears.map((financialYear, index) => {
            const radioId = `${FINANCIAL_YEAR_RADIO_NAME}-${index}`
            const isSelected = selectedOption === financialYear

            return (
              <li key={financialYear} role='listitem' className='list-none m-0 p-0'>
                <label
                  htmlFor={radioId}
                  className='relative flex items-center justify-between px-5 py-6 cursor-pointer w-full bg-transparent border-none text-left'
                >
                  <input
                    id={radioId}
                    type='radio'
                    name={FINANCIAL_YEAR_RADIO_NAME}
                    value={financialYear}
                    checked={isSelected}
                    onChange={() => {
                      setSelectedOption(financialYear)
                    }}
                    aria-label={financialYear}
                    // aria-description='Double tap to select financial year'
                    className='absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0'
                  />
                  <span aria-hidden='true' className='text-[14px] font-[500] leading-[1.2] text-[#232323]'>
                    {financialYear}
                  </span>
                  <span
                    aria-hidden='true'
                    className={`pointer-events-none flex items-center justify-center w-5 h-5 shrink-0 rounded-full border ${isSelected ? 'border-[#232323] bg-[#232323]' : 'border-[#D0D5DD]'
                      }`}
                  >
                    {isSelected ? (
                      <span className='text-[16px] leading-none text-white'>
                        <svg xmlns='http://www.w3.org/2000/svg' width='9' height='7' viewBox='0 0 9 7' fill='none'>
                          <path
                            d='M0.75 3.78571L3.42857 6.10714L7.89286 0.75'
                            stroke='white'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </span>
                    ) : null}
                  </span>
                </label>
                {index < financialYears.length - 1 ? (
                  <div aria-hidden='true' className='h-px bg-[#EBF2F2]' />
                ) : null}
              </li>
            )
          })}
        </ul>

        <div
          style={{ paddingBottom: `calc(${getBottomPadding()}px + 8px)` }}
          className='px-5 pt-4'
        >
          {/*
            Tax summary_B08 (iOS Hint_text_required):
            Accessible name stays the visible "Confirm"; outcome hint via aria-description
            so VoiceOver announces: "Confirm, button, double tap to confirm selected financial year"
          */}
          <PrimaryButton
            onClick={() => {
              onConfirm(selectedOption)
              onDismiss()
            }}
            label='Confirm'
            aria-description='Double tap to confirm selected financial year'
            primaryButtonClassName='!h-[56px] !text-[16px] !font-[500] !rounded-[16px]'
          />
        </div>
      </div>
    </BottomSheetContent>
  )
}
